import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@lib/utils/cn';

type Variant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'outline'
  | 'success'
  | 'warning'
  | 'info'
  | 'accent'
  | 'neutral'
  | 'soft-success'
  | 'soft-warning'
  | 'soft-info'
  | 'soft-danger'
  | 'soft-accent';

type Size = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-brand text-brand-foreground hover:bg-brand-hover active:brightness-95 shadow-sm focus-visible:ring-brand',
  secondary:
    'bg-bg-muted text-fg hover:bg-border focus-visible:ring-fg-subtle',
  ghost:
    'text-fg hover:bg-bg-muted focus-visible:ring-fg-subtle',
  danger:
    'bg-danger text-danger-foreground hover:bg-danger-hover active:brightness-95 shadow-sm focus-visible:ring-danger',
  outline:
    'border border-border text-fg hover:bg-bg-muted hover:border-fg-subtle focus-visible:ring-fg-subtle',
  success:
    'bg-success text-success-foreground hover:bg-success-hover active:brightness-95 shadow-sm focus-visible:ring-success',
  warning:
    'bg-warning text-warning-foreground hover:bg-warning-hover active:brightness-95 shadow-sm focus-visible:ring-warning',
  info:
    'bg-info text-info-foreground hover:bg-info-hover active:brightness-95 shadow-sm focus-visible:ring-info',
  accent:
    'bg-accent text-accent-foreground hover:bg-accent-hover active:brightness-95 shadow-sm focus-visible:ring-accent',
  neutral:
    'bg-neutral text-neutral-foreground hover:bg-neutral-hover active:brightness-95 shadow-sm focus-visible:ring-neutral',
  'soft-success':
    'bg-success-muted text-success hover:brightness-95 dark:hover:brightness-110 focus-visible:ring-success',
  'soft-warning':
    'bg-warning-muted text-warning hover:brightness-95 dark:hover:brightness-110 focus-visible:ring-warning',
  'soft-info':
    'bg-info-muted text-info hover:brightness-95 dark:hover:brightness-110 focus-visible:ring-info',
  'soft-danger':
    'bg-danger-muted text-danger hover:brightness-95 dark:hover:brightness-110 focus-visible:ring-danger',
  'soft-accent':
    'bg-accent-muted text-accent hover:brightness-95 dark:hover:brightness-110 focus-visible:ring-accent',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
  icon: 'h-10 w-10 p-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading,
      leftIcon,
      rightIcon,
      disabled,
      children,
      type = 'button',
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
          <path className="opacity-90" d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      ) : (
        leftIcon
      )}
      {children}
      {!loading && rightIcon}
    </button>
  ),
);
Button.displayName = 'Button';
